import React, { useEffect, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useCollection } from '../data/firestore';
import { CONCEPT_TOURS } from '../data/consoleModules';
import { askNova } from '../components/ConsoleHeader';
import { useAuth, signOut } from '../AuthContext';
import { MarkdownLite } from './Plan';
import { AMBIENT_SCREENS } from '../components/ambient';
import { PORTFOLIO_SCREENS } from '../components/portfolio';
import './ConceptPage.css';

// Gated concept pages (/concepts/:page). Served from the Firestore
// `conceptSections` collection (allowlist-gated in the rules, same posture as
// /docs); the markdown master is docs/concepts/<page>.md, synced by
// scripts/sync-concept-page.mjs --page <page>. Not linked from public nav;
// robots Disallow. One registry entry per page: the live `screen:<id>`
// figures it may embed. The NoVA tour for a page is CONCEPT_TOURS[page].
export const CONCEPT_PAGES = {
  'ambient-screen': { screens: AMBIENT_SCREENS },
  portfolio: { screens: PORTFOLIO_SCREENS },
};

const FIG = /^!\[([^\]]*)\]\(([^)]*)\)\s*$/;
const CAP = /^\*(.+)\*$/;

// MarkdownLite has no image syntax, so split the body on figure lines:
// `![alt](src)` + an optional italic `*caption*` line become a <figure>, the
// text between them goes through MarkdownLite unchanged. A src of
// `screen:<id>` renders the live React figure registered for the page
// instead of an <img> (unknown ids render the caption only).
// `focus` = { fig, frame, view } from the URL (?fig=&frame=&view=) — the
// figure whose id matches gets the frame as a controlled prop, which is how
// NoVA drives a carousel during the guided tour (CONCEPT_TOURS).
const SCREEN = /^screen:(.+)$/;

export function MarkdownFigures({ text, focus, screens = AMBIENT_SCREENS }) {
  const lines = (text || '').split('\n');
  const out = [];
  let buf = [];
  const flush = () => {
    if (buf.join('').trim()) out.push(<MarkdownLite key={out.length} text={buf.join('\n')} />);
    buf = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].trim().match(FIG);
    if (!m) { buf.push(lines[i]); continue; }
    flush();
    const cap = (lines[i + 1] || '').trim().match(CAP);
    if (cap) i++;
    const sc = m[2].match(SCREEN);
    const Screen = sc ? screens[sc[1]] : null;
    const focused = sc && focus && focus.fig === sc[1];
    out.push(
      <figure className={`cs-concept-figure ${sc ? 'is-screen' : ''}`} key={out.length} id={sc ? `fig-${sc[1]}` : undefined}>
        {sc ? (Screen && <Screen frame={focused ? focus.frame : null} view={focused ? focus.view : undefined} />) : <img src={m[2]} alt={m[1]} loading="lazy" />}
        {cap && <figcaption><em>{cap[1]}</em></figcaption>}
      </figure>
    );
  }
  flush();
  return <>{out}</>;
}

// Highlight the last section whose heading has scrolled past the nav line.
function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0] || '');
  useEffect(() => {
    if (!ids.length) return undefined;
    const update = () => {
      let cur = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 200) cur = id;
      }
      setActive(cur);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [ids.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  return active;
}

export default function ConceptPage() {
  const { page: PAGE } = useParams();
  const def = CONCEPT_PAGES[PAGE];
  const { user } = useAuth();
  const { docs, error } = useCollection('conceptSections', { orderBy: 'order', dir: 'asc' });
  const rows = (docs || []).filter((d) => d.page === PAGE);
  const header = rows.find((d) => d.order === 0) || null;
  const sections = rows.filter((d) => d.order > 0);
  const active = useActiveSection(sections.map((s) => s.id));

  // Deep link / tour target: ?sec=<sectionId> scrolls the section into view
  // (or the figure when ?fig= is set); ?frame= is 0-based.
  const [params] = useSearchParams();
  const sec = params.get('sec');
  const fig = params.get('fig');
  const frameParam = params.get('frame');
  const focus = fig ? { fig, frame: frameParam == null ? null : Math.max(0, Number(frameParam) || 0), view: params.get('view') || undefined } : null;
  const ready = !!header;
  useEffect(() => {
    if (!ready || (!sec && !fig)) return undefined;
    // Images above the target keep loading for a while and shift the layout
    // under a single scroll, so re-aim a few times (instant after the first).
    const go = (smooth) => {
      const el = (fig && document.getElementById(`fig-${fig}`)) || (sec && document.getElementById(sec));
      if (el) el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: fig ? 'center' : 'start' });
    };
    const ts = [setTimeout(() => go(true), 80), setTimeout(() => go(false), 900), setTimeout(() => go(false), 2200)];
    return () => ts.forEach(clearTimeout);
  }, [ready, sec, fig, frameParam]);
  const tour = CONCEPT_TOURS[PAGE];

  if (!def) return <Navigate to="/" replace />;
  if (error) {
    const denied = /permission/i.test(error);
    return (
      <div className="cs-page">
        <div className="cs-empty" style={{ maxWidth: 520 }}>
          {denied ? (
            <>
              <p style={{ marginTop: 0 }}>
                <strong>{user?.email}</strong> isn't approved for this concept
                page. Access is invite-only — contact Nventr to have your email
                added.
              </p>
              <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => signOut()}>
                Sign out
              </button>
            </>
          ) : (
            <>Couldn't load the page: {error}</>
          )}
        </div>
      </div>
    );
  }
  if (docs === null) {
    return (
      <div className="cs-page">
        <div className="cs-empty">Loading the concept…</div>
      </div>
    );
  }
  if (!header) {
    return (
      <div className="cs-page">
        <div className="cs-empty">This concept page hasn't been published yet.</div>
      </div>
    );
  }

  return (
    <div className="cs-concept">
      <header className="cs-concept-hero">
        {header.hero && <img src={header.hero} alt={header.heroCaption || header.title} />}
        <div className="cs-concept-hero-text">
          <p className="cs-concept-kicker">{header.version}</p>
          <h1>{header.title}</h1>
          {header.subtitle && <p className="cs-concept-subtitle">{header.subtitle}</p>}
          {tour && (
            <button type="button" className="cs-btn cs-btn-outline cs-btn-sm cs-concept-tour-btn" onClick={() => askNova(tour.prompt)}>
              ◈ Walk me through it with NoVA
            </button>
          )}
        </div>
      </header>
      {header.heroCaption && (
        <p className="cs-concept-hero-caption"><em>{header.heroCaption}</em></p>
      )}

      <div className="cs-concept-body">
        <nav className="cs-concept-rail" aria-label="Contents">
          <p className="cs-concept-rail-label">Contents</p>
          {sections.map((s) => (
            <a key={s.id} href={`#${s.id}`} className={s.id === active ? 'is-active' : ''}>{s.title}</a>
          ))}
        </nav>

        <div className="cs-concept-main cs-plan">
          {header.intro && (
            <section className="cs-plan-section cs-concept-intro">
              <MarkdownLite text={header.intro} />
            </section>
          )}

          <nav className="cs-plan-toc cs-concept-toc-inline">
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`}>{s.title}</a>
            ))}
          </nav>

          {sections.map((s) => (
            <section key={s.id} id={s.id} className="cs-plan-section">
              <h2>{s.title}</h2>
              <MarkdownFigures text={s.body} focus={focus} screens={def.screens} />
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
